/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { useQuery } from '@apollo/client';
import { CollectionsModule, unifiedModuleColors } from '@coursera-web/cds-labs';
import type { Props as CollectionModuleProps } from '@coursera-web/cds-labs';

import { breakpoints } from '@coursera/cds-core';

import { DEFAULT_COLLECTION_SLUG, ENROLL_BUTTON_TESTID } from 'bundles/coursera-plus-common/components/constants';
import CTAContentful from 'bundles/coursera-plus-landing-page/components/ctaContentful/CTAContentful';
import { CTA_LOCATIONS } from 'bundles/coursera-plus-landing-page/contentful/constants';
import { useLandingPageExtended } from 'bundles/coursera-plus-landing-page/contentfulData/useLandingPageData';
import { mapCatalogCollectionToCarouselCollection } from 'bundles/coursera-plus-landing-page/courseraPlusCollections/utils';
import { CATALOG_COLLECTION_PAGE_QUERY } from 'bundles/coursera-plus-landing-page/queries';
import type {
  CatalogCollectionPageQuery,
  CatalogCollectionPageQueryVariables,
} from 'bundles/coursera-plus-landing-page/queries/__generated__/CatalogCollectionPageQuery';
import inServerContext from 'bundles/ssr/util/inServerContext';

import _t from 'i18n!nls/coursera-plus-landing-page';

type Props = {
  collectionSlug?: string;
  titleOverride?: string;
};

const styles = {
  /**
   * the container styles at different breakpoints handle PageGridContainer-related css issues.
   * because the module above this container does not have top and bottom padding,
   * this module has incorrectly-applied negative margin. These styles undo that negative margin.
   */
  container: css`
    ${breakpoints.down('xl')} {
      margin: calc(var(--cds-spacing-300) + var(--cds-spacing-200)) 0 0;
    }

    ${breakpoints.down('md')} {
      margin: calc(var(--cds-spacing-200) - 1px) 0 0;
    }

    ${breakpoints.down('sm')} {
      margin: calc(var(--cds-spacing-100) - 1px) 0 0;

      [data-e2e='${ENROLL_BUTTON_TESTID}'] {
        margin-top: var(--cds-spacing-100);
      }
    }

    ${breakpoints.down('xs')} {
      margin: calc(var(--cds-spacing-50) - 1px) 0 0;
    }
  `,
};

const CourseraPlusProductCollection = ({ collectionSlug, titleOverride }: Props) => {
  const { landingPage } = useLandingPageExtended();
  const { data: collectionData } = useQuery<CatalogCollectionPageQuery, CatalogCollectionPageQueryVariables>(
    CATALOG_COLLECTION_PAGE_QUERY,
    {
      skip: inServerContext,
      variables: {
        slug: collectionSlug ?? DEFAULT_COLLECTION_SLUG,
      },
    }
  );
  const catalogCollection = collectionData?.CatalogCollectionPagesV1Resource?.bySlug?.elements?.[0];
  const ctaPieces = landingPage.heroCtaPartsCollection?.items.filter((ctaPart) =>
    ctaPart?.presentation?.toLowerCase().includes('button')
  );

  if (!catalogCollection) return null;

  const collection = mapCatalogCollectionToCarouselCollection(catalogCollection);
  const propsToModule: CollectionModuleProps = {
    headline: titleOverride ?? _t('Career skills that work'),
    backgroundColor: unifiedModuleColors.grey,
    showRoundedCorners: true,
    collections: [collection],
    layout: 'text3/image9',
    lazyLoadImage: true,
  };

  return (
    <div css={styles.container}>
      <CollectionsModule
        {...propsToModule}
        customCtaButton={<CTAContentful ctaPieces={ctaPieces} ctaLocation={CTA_LOCATIONS.collections} />}
      />
    </div>
  );
};

export default CourseraPlusProductCollection;
